import { Component, Injectable } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { BehaviorSubject } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { ProductService } from '../../_ecomservices/product.service'; // Import the ProductService

/**
 * Node for category item
 */
export class CategoryItemNode {
  children: CategoryItemNode[];
  category_name: string;
  id: number;
  slug: string;
  cat_img: string;
  parent_category_id: number;
}

/** Flat category item node with expandable and level information */
export class CategoryItemFlatNode {
  category_name: string;
  level: number;
  expandable: boolean;
  id: number;
}

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a category.
 */
@Injectable()
export class ChecklistDatabase {
  dataChange = new BehaviorSubject<CategoryItemNode[]>([]);

  get data(): CategoryItemNode[] { return this.dataChange.value; }

  constructor(private productService: ProductService) {
    this.initialize();
  }

  initialize() {
    // Fetch categories data from the API
    this.get_active_category();
  }

  // Function to fetch categories data
  get_active_category() {
    this.productService.get_categories_hierarchy().subscribe(data => {
      const CATEGORY_API_DATA = data['category_list'];
      const dataTree = this.buildFileTree(CATEGORY_API_DATA, 0);
      // Notify the change with the fetched data
      this.dataChange.next(dataTree);
    });
  }

  /**
   * Build the category structure tree. The `value` is the API object.
   * The return value is the list of `CategoryItemNode`.
   */
  buildFileTree(obj: { [key: string]: any }, level: number): CategoryItemNode[] {
    
    return Object.keys(obj).reduce<CategoryItemNode[]>((accumulator, key) => {
      const value = obj[key];
      const node = new CategoryItemNode();
      //node.category_name = key;

      if (value != null) {
        if (typeof value === 'object') {
          node.children = this.buildFileTree(value, level + 1);
        } else {
          node.category_name = value;
        }
      }

      return accumulator.concat(node);
    }, []);
  }

  insertItem(parent: CategoryItemNode, name: string) {
    if (parent.children) {
      parent.children.push({category_name: name} as CategoryItemNode);
      this.dataChange.next(this.data);
    }
  }

  updateItem(node: CategoryItemNode, name: string) {
    node.category_name = name;
    this.dataChange.next(this.data);
  }
}

/**
 * @title Tree with checkboxes
 */
@Component({
  selector: 'app-category-tree',
  templateUrl: './category-tree.component.html',
  styleUrls: ['./category-tree.component.css'],
  providers: [ChecklistDatabase]
})
export class CategoryTreeComponent {
  flatNodeMap = new Map<CategoryItemFlatNode, CategoryItemNode>();
  nestedNodeMap = new Map<CategoryItemNode, CategoryItemFlatNode>();

  selectedParent: CategoryItemFlatNode | null = null;
  newItemName = '';

  treeControl: FlatTreeControl<CategoryItemFlatNode>;
  treeFlattener: MatTreeFlattener<CategoryItemNode, CategoryItemFlatNode>;
  dataSource: MatTreeFlatDataSource<CategoryItemNode, CategoryItemFlatNode>;

  checklistSelection = new SelectionModel<CategoryItemFlatNode>(true);

  constructor(private database: ChecklistDatabase) {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<CategoryItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    database.dataChange.subscribe(data => {
      this.dataSource.data = data;
    });
  }

  getLevel = (node: CategoryItemFlatNode) => node.level;
  isExpandable = (node: CategoryItemFlatNode) => node.expandable;
  getChildren = (node: CategoryItemNode): CategoryItemNode[] => node.children;

  hasChild = (_: number, _nodeData: CategoryItemFlatNode) => _nodeData.expandable;
  hasNoContent = (_: number, _nodeData: CategoryItemFlatNode) => _nodeData.category_name === '';

  transformer = (node: CategoryItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.category_name === node.category_name
        ? existingNode
        : new CategoryItemFlatNode();
    flatNode.category_name = node.category_name; // Only showing category_name
    flatNode.level = level;
    flatNode.expandable = !!node.children;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }

  descendantsAllSelected(node: CategoryItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return descendants.every(child => this.checklistSelection.isSelected(child));
  }

  descendantsPartiallySelected(node: CategoryItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return descendants.some(child => this.checklistSelection.isSelected(child)) && !this.descendantsAllSelected(node);
  }

  todoItemSelectionToggle(node: CategoryItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);
    this.checkAllParentsSelection(node);
  }

  todoLeafItemSelectionToggle(node: CategoryItemFlatNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
  }

  checkAllParentsSelection(node: CategoryItemFlatNode): void {
    let parent: CategoryItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  checkRootNodeSelection(node: CategoryItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every(child => this.checklistSelection.isSelected(child));
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  getParentNode(node: CategoryItemFlatNode): CategoryItemFlatNode | null {
    const currentLevel = this.getLevel(node);
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  addNewItem(node: CategoryItemFlatNode) {
    const parentNode = this.flatNodeMap.get(node);
    this.database.insertItem(parentNode!, '');
    this.treeControl.expand(node);
  }

  saveNode(node: CategoryItemFlatNode, itemValue: string) {
    const nestedNode = this.flatNodeMap.get(node);
    this.database.updateItem(nestedNode!, itemValue);
  }

  // Display the selected categories
  displaySelectedCategories() {
    const selectedCategories = this.checklistSelection.selected.map(node => node.category_name);
    alert('Selected Categories: ' + selectedCategories.join(', '));
  }
}
